import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Compass, ChevronDown, LogOut, Moon, Sun, Palette, KeyRound, Bell, HelpCircle,
  PanelLeftClose, PanelLeftOpen, Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeProvider";
import { api } from "@/lib/api";
import { THEMES } from "@/lib/themes";
import { getIcon } from "@/lib/icons";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import AssistantWidget from "@/components/AssistantWidget";
import JornadaGate from "@/components/JornadaGate";
import JornadaQuickAccess from "@/components/JornadaQuickAccess";
import CalendarQuickAccess from "@/components/CalendarQuickAccess";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";

const NotificationsBell = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });
  const items = data?.items || [];
  const unread = data?.unread || 0;
  const readMut = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAllMut = useMutation({
    mutationFn: async () => (await api.post("/notifications/read-all")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const onClick = (n) => {
    if (!n.read) readMut.mutate(n.id);
    if (n.link) { try { navigate(new URL(n.link).pathname); } catch { navigate(n.link); } }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-bell" aria-label="Avisos">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground" data-testid="notifications-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-semibold">Avisos</span>
          {unread > 0 && (
            <button className="text-xs font-medium text-primary hover:underline" onClick={() => readAllMut.mutate()} data-testid="notifications-read-all">
              Marcar todo leído
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        {items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">Sin avisos por ahora.</p>
        ) : (
          <ScrollArea className="max-h-80">
            {items.map((n) => (
              <button
                key={n.id} onClick={() => onClick(n)} data-testid={`notification-${n.id}`}
                className={cn("flex w-full flex-col items-start gap-0.5 border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted", !n.read && "bg-primary/5")}
              >
                <span className="flex w-full items-center gap-2">
                  {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                  <span className="text-sm font-semibold leading-tight">{n.title}</span>
                </span>
                <span className="text-xs leading-snug text-muted-foreground">{n.body}</span>
              </button>
            ))}
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SidebarItem = ({ item, active, onNavigate, collapsed, currentView, badge = 0 }) => {
  const Icon = getIcon(item.icon);
  if (collapsed) {
    return (
      <button
        onClick={() => onNavigate(item.id, item.view)}
        data-testid={`nav-${item.id}${item.view ? `-${item.view}` : ""}`}
        title={item.label}
        className={cn(
          "relative flex w-full items-center justify-center rounded-lg p-2.5 transition-colors duration-150",
          active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        {active && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />}
        <Icon className={cn("h-5 w-5 shrink-0", active && "text-primary")} />
        {badge > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground" data-testid={`nav-badge-${item.id}`}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>
    );
  }
  if (item.children && item.children.length > 0) {
    const kids = item.children.map((c) => (typeof c === "string" ? { label: c } : c));
    const isKidActive = (k) => active && (k.module ? k.module === item.id : (currentView || "") === (k.view || ""));
    return (
      <Collapsible defaultOpen={active}>
        <CollapsibleTrigger asChild>
          <button
            data-testid={`nav-${item.id}`}
            className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
            {badge > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground" data-testid={`nav-badge-${item.id}`}>
                {badge > 99 ? "99+" : badge}
              </span>
            )}
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180", badge > 0 ? "ml-1.5" : "ml-auto")} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-[1.45rem] mt-0.5 space-y-0.5 border-l pl-3">
            {kids.map((k, i) => (
              <button
                key={`${k.label}-${i}`}
                onClick={() => onNavigate(k.module || item.id, k.view)}
                data-testid={`nav-${item.id}-${k.module || k.view || "default"}`}
                className={cn(
                  "block w-full rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors",
                  isKidActive(k) ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {k.label}
              </button>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }
  return (
    <button
      onClick={() => onNavigate(item.id, item.view)}
      data-testid={`nav-${item.id}${item.view ? `-${item.view}` : ""}`}
      className={cn(
        "relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150",
        active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {active && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />}
      <Icon className={cn("h-4 w-4 shrink-0", active && "text-primary")} />
      <span className="truncate">{item.label}</span>
      {badge > 0 && (
        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground" data-testid={`nav-badge-${item.id}`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
};

const SidebarGroup = ({ group, collapsed, active, isItemActive, onNavigate, currentView, badgeFor }) => {
  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(`sofi_group_${group.label}`);
    if (stored !== null) return stored === "1";
    // Default: solo el grupo BIENVENIDA queda abierto al entrar por primera vez;
    // el resto se colapsan para reducir ruido visual.
    return (group.label || "").toUpperCase() === "BIENVENIDA";
  });
  const groupHasActive = group.items.some((it) => isItemActive(it));
  const isOpen = collapsed ? true : (groupHasActive || open);
  const toggle = () => {
    const nx = !open;
    setOpen(nx);
    localStorage.setItem(`sofi_group_${group.label}`, nx ? "1" : "0");
  };
  if (collapsed) {
    return (
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <SidebarItem key={`${item.id}${item.view || ""}`} item={item} active={isItemActive(item)} onNavigate={onNavigate} collapsed={collapsed} currentView={currentView} badge={badgeFor(item.id)} />
        ))}
      </div>
    );
  }
  return (
    <Collapsible open={isOpen} onOpenChange={toggle}>
      <CollapsibleTrigger asChild>
        <button
          data-testid={`sidebar-group-${group.label.replace(/\s+/g, "-").toLowerCase()}`}
          className="group mb-1.5 flex w-full items-center justify-between rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <span>{group.label}</span>
          <ChevronDown className={cn("h-3 w-3 shrink-0 transition-transform duration-200", isOpen && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <SidebarItem key={`${item.id}${item.view || ""}`} item={item} active={isItemActive(item)} onNavigate={onNavigate} collapsed={collapsed} currentView={currentView} badge={badgeFor(item.id)} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function AppShell() {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const location = useLocation();
  const { user, navigation, logout, modules, isImpersonating } = useAuth();
  const { theme, setTheme, dark, setDark } = useTheme();
  const [company, setCompany] = useState("VDV");
  const [pwOpen, setPwOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sofi_sidebar_collapsed") === "1");
  // Drawer del menú en móvil (<md). En desktop la sidebar es persistente.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Cierra el drawer móvil automáticamente al navegar a otra ruta.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  // Migración one-shot (v2): limpia el estado previo de grupos (que por defecto
  // los tenía todos abiertos) para que aplique el nuevo default: solo BIENVENIDA
  // abierta al entrar. Después el usuario puede volver a personalizar y su
  // preferencia se guarda como antes.
  useEffect(() => {
    if (localStorage.getItem("sofi_sidebar_groups_v2") === "1") return;
    const keys = Object.keys(localStorage).filter((k) => k.startsWith("sofi_group_"));
    keys.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem("sofi_sidebar_groups_v2", "1");
  }, []);

  const { data: unreadData } = useQuery({
    queryKey: ["chat-unread"],
    queryFn: async () => (await api.get("/chat/unread")).data,
    enabled: (modules || []).includes("chat"),
    refetchInterval: 15000,
  });
  const chatUnread = unreadData?.count || 0;

  const { data: ccUnreadData } = useQuery({
    queryKey: ["cc-unread-nav"],
    queryFn: async () => (await api.get("/client-chat/unread")).data,
    enabled: (modules || []).includes("chat_clientes") || (modules || []).includes("chat_support"),
    refetchInterval: 15000,
  });
  const ccPresale = ccUnreadData?.presale || 0;
  const ccSupport = ccUnreadData?.support || 0;

  const { data: assignedLeadsData } = useQuery({
    queryKey: ["crm-leads-assigned-new"],
    queryFn: async () => (await api.get("/crm-leads/my/assigned-new")).data,
    enabled: (modules || []).includes("pipeline"),
    refetchInterval: 30000,
  });
  const pipelineNew = assignedLeadsData?.count || 0;

  const badgeFor = (id) => (id === "chat" ? chatUnread : id === "chat_clientes" ? ccPresale : id === "chat_support" ? ccSupport : id === "pipeline" ? pipelineNew : 0);

  const toggleCollapsed = () => setCollapsed((c) => {
    const n = !c;
    localStorage.setItem("sofi_sidebar_collapsed", n ? "1" : "0");
    return n;
  });

  // must_change_password no debe forzar el diálogo cuando estamos impersonando
  // (la mutación estaría bloqueada por el middleware read-only).
  const forced = !!user?.must_change_password && !isImpersonating;
  useEffect(() => {
    if (forced) setPwOpen(true);
  }, [forced]);

  const go = (id, view) => {
    if (id === "asistente_reservas") return navigate("/asistente");
    if (id === "b2b") return navigate("/comercial-b2b");
    if (id === "cashflow_app") return navigate("/cashflow-app");
    if (id === "facturas_app") return navigate("/facturas-app");
    if (id === "b2c_app") return navigate("/b2c-app");
    navigate(`/app/${id}${view ? `?view=${view}` : ""}`);
  };

  const allItems = navigation.flatMap((g) => g.items);
  const currentView = new URLSearchParams(location.search).get("view") || "";
  // Con items "flat" (varios comparten module id) elige el que casa con la view activa.
  const current = allItems.find((it) => it.id === moduleId && it.view && (it.view === currentView || (!currentView && it.default)))
    || allItems.find((it) => it.id === moduleId);
  const title = current?.label || "SOFI 2.0";
  // Activo para items "flat" con view propia (Portal del empleado).
  const isItemActive = (it) => {
    if (it.id !== moduleId) return false;
    if (!it.view) return true;
    return currentView === it.view || (!currentView && it.default);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside className={cn(
        "hidden shrink-0 flex-col border-r bg-card md:flex transition-[width] duration-200",
        collapsed ? "w-16" : "w-[272px]"
      )}>
        <div className={cn("flex h-14 items-center border-b", collapsed ? "justify-center px-2" : "gap-2.5 px-4")}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Compass className="h-5 w-5" />
          </div>
          {!collapsed && (
            <>
              <div className="leading-tight">
                <p className="font-heading text-sm font-extrabold tracking-tight">SOFI 2.0</p>
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">ERP · CRM</p>
              </div>
              <Button variant="ghost" size="icon" className="ml-auto h-7 w-7" onClick={toggleCollapsed} data-testid="sidebar-collapse-button" aria-label="Contraer menú" title="Contraer menú">
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
        {collapsed && (
          <div className="flex justify-center border-b py-1.5">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleCollapsed} data-testid="sidebar-expand-button" aria-label="Expandir menú" title="Expandir menú">
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          </div>
        )}

        <ScrollArea className="flex-1">
          <nav className={cn("p-3", collapsed ? "space-y-1" : "space-y-3")} data-testid="sidebar-nav">
            {navigation.map((group) => (
              <SidebarGroup
                key={group.label}
                group={group}
                collapsed={collapsed}
                isItemActive={isItemActive}
                onNavigate={go}
                currentView={currentView}
                badgeFor={badgeFor}
              />
            ))}
          </nav>
        </ScrollArea>

        <div className="border-t p-3">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.full_name)}</AvatarFallback></Avatar>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={logout} data-testid="logout-button" aria-label="Cerrar sesión" title="Cerrar sesión">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.full_name)}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.full_name}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.role_name}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={logout} data-testid="logout-button" aria-label="Cerrar sesión">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonationBanner />
        <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b bg-card/80 px-3 backdrop-blur sm:gap-3 sm:px-4">
          {/* Hamburguesa · SOLO móvil */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden" data-testid="mobile-nav-trigger" aria-label="Abrir menú">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-[280px] flex-col p-0" data-testid="mobile-nav-drawer">
              <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
              <div className="flex h-14 items-center gap-2.5 border-b px-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Compass className="h-5 w-5" />
                </div>
                <div className="leading-tight">
                  <p className="font-heading text-sm font-extrabold tracking-tight">SOFI 2.0</p>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">ERP · CRM</p>
                </div>
              </div>
              <ScrollArea className="flex-1">
                <nav className="space-y-3 p-3" data-testid="mobile-nav">
                  {navigation.map((group) => (
                    <SidebarGroup
                      key={group.label}
                      group={group}
                      collapsed={false}
                      isItemActive={isItemActive}
                      onNavigate={go}
                      currentView={currentView}
                      badgeFor={badgeFor}
                    />
                  ))}
                </nav>
              </ScrollArea>
              <div className="border-t p-3">
                <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.full_name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="truncate text-sm font-semibold">{user?.full_name}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.role_name}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={logout} data-testid="mobile-logout" aria-label="Cerrar sesión">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <p className="truncate text-sm font-semibold" data-testid="page-title">{title}</p>
          <div className="ml-auto flex items-center gap-1.5">
            <JornadaQuickAccess />

            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" aria-label="Cambiar tema">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <CalendarQuickAccess />
            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-muted" data-testid="user-menu-button">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.full_name)}</AvatarFallback></Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="leading-tight">
                  <p className="truncate text-sm font-semibold">{user?.full_name}</p>
                  <p className="truncate text-xs font-normal text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="mr-2 h-4 w-4" /> Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto scroll-slim px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} forced={forced} />
      <AssistantWidget />
      <JornadaGate />
    </div>
  );
}
